'use client'
import React, {useEffect, useState} from 'react'
import {
  Box,
  createTheme,
  CssBaseline,
  ThemeProvider,
} from '@mui/material'
import Image from "next/image"

import { usePathname } from "next/navigation"
import { useTranslation } from "@/app/i18n/client"
import { useColorScheme } from "@mui/material/styles"
import useLocalStorage from "@/hooks/navigator/useLocalStorage"

import { protectedRoutes } from "@/configs/routes"
import { TranslationContext } from "@/configs/context"
import { defaultNS, fallbackLng } from "@/app/i18n/settings"

import { Toaster } from "react-stacked-toast"
import { SessionProvider } from "next-auth/react"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"

import DefaultAppBar from "@/ui/layouts/DefaultAppBar"
import { AppProvider } from "@toolpad/core/AppProvider"
import OnboardingWrapper from "@/ui/components/common/OnboardingWrapper"

import { appLinks } from "@/lib/links"
import { APP_THEME } from "@/configs/theme"
import { GLOBAL_PATHS } from '@/lib/globals'


/**
 * Ce composant est la base de construction de toutes les interfaces de l'application
 * @param children
 * @constructor
 * { children, lng }: Readonly<{ children: React.ReactNode, lng: string }>
 */
export default function AppLayout({ children }: Readonly<{ children: React.ReactNode }>) {
  const pathname = usePathname()
  const { colorScheme } = useColorScheme()
  const { t, i18n } = useTranslation(fallbackLng, defaultNS, {})
  
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false)
  const [, setOnboardingSeen] = useLocalStorage<boolean>("onboardingSeen", false)

  const muiDefaultTheme = React.useMemo(() => {
    const direction = i18n.language !== 'ar' ? 'ltr' : 'rtl'

    return createTheme({
      direction,
      palette: {
        // @ts-expect-error:next-line
        mode: colorScheme as 'light' | 'dark',
        ...(colorScheme === 'light' ? APP_THEME.LIGHT.palette : APP_THEME.DARK.palette),
      }
    })
  }, [colorScheme, i18n.language, ])

  const showAppBar: boolean = React.useMemo(() => (
    !protectedRoutes.find(
      value => new RegExp(value).test(pathname ?? '')
    )
  ), [pathname])

  useEffect(() => {
    if (localStorage) {
      if (localStorage.getItem("onboardingSeen") !== "true"
        && (
          !showAppBar
          || pathname.startsWith(appLinks.common.LOGIN)
        )
      ) {
        setTimeout(() => { setShowOnboarding(true) }, 1000)
      } else if (showOnboarding) {
        setShowOnboarding(false)
      }

      // @ts-expect-error:next-line
      window.dataLayer = window.dataLayer || []
      // eslint-disable-next-line prefer-rest-params
      // @ts-expect-error:next-line
      // eslint-disable-next-line prefer-rest-params
      function gtag(){dataLayer.push(arguments)}
      // @ts-expect-error:next-line
      gtag('js', new Date())
      // eslint-disable-next-line
      // @ts-expect-error:next-line
      gtag('config', process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_KEY)
    }

    // Souscription du service worker côté client
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
      .then(function() {
        // console.log("Service Worker Registered ", registration);
      })
      .catch(function(err) {
        console.log("Service Worker Failed to Register", err);
      })

      navigator
      .serviceWorker
      .addEventListener('onerror', function (error) {
        console.log(error)
      })

      navigator.serviceWorker.addEventListener('controllerchange', ()  =>{
        //window.location.reload()
      })
    }
  }, [])

  return (
    <React.Suspense fallback={<div />}>
      <ThemeProvider theme={muiDefaultTheme}>
        <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <AppProvider
              // branding={TollPadBranding}
              branding={{
                title: "",
                logo: (
                  <Image
                    width={128}
                    height={128}
                    alt="SarkiAi"
                    loading="eager"
                    priority={true}
                    src={`${GLOBAL_PATHS.PUBLIC}sarki-ai-logo-dark.svg`}
                  />
                )
              }}
              theme={{ 
                dark: createTheme(APP_THEME.DARK),
                light: createTheme(APP_THEME.LIGHT)
               }}
            >
              <SessionProvider>
                <TranslationContext.Provider value={{ t, lang: i18n.language, i18n }}>
                  <Box 
                    suppressHydrationWarning
                    sx={{ flexGrow: 1, "& *": { fontFamily: '"Poppins" !important' } }}
                  >
                    {showAppBar ? <DefaultAppBar/> : null}

                    {showOnboarding && 
                    <OnboardingWrapper onClose={() => {
                      setShowOnboarding(false)
                      setOnboardingSeen(true)
                    }} />}

                    <Toaster
                      position="center"
                      toastOptions={{
                        duration: 3 * 1000,
                        style: {
                          color: '#fff',
                          fontSize: "12px",
                          fontWeight: "normal",
                          backgroundColor: 'rgba(17, 25, 40, 0.85)',
                        },
                      }}
                    />
                    <Box className="root-container">
                      {children}
                    </Box>
                  </Box>
                </TranslationContext.Provider>
              </SessionProvider>
            </AppProvider>
          </LocalizationProvider>
      </ThemeProvider>
    </React.Suspense>
  )
}